import { useCallback, useState } from 'react';
import { Bookmark, Heart, Map, Sparkles } from 'lucide-react';
import type { NavigationConfig } from '@core/ui/navigation';
import { buildClassName } from 'utils/build-class-name';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { MobileSavedSearchSheet } from 'components/pages/area-page-v2/panes/mobile-saved-search-sheet';
import type { MapBounds } from 'domain/saved-search';
import type { RailDestination } from '../types';
import { RailMenu } from './rail-menu';
import styles from './mobile-nav-bar.module.scss';

import type { RailLocaleMenu } from './use-rail-menu-config';

const DEFAULT_AVAILABLE_DESTINATIONS: RailDestination[] = ['map', 'collections', 'insights'];

export type MobileNavBarProps = {
  destination: RailDestination;
  onOpenMap: () => void;
  onOpenCollections: () => void;
  onOpenInsights: () => void;
  onOpenSavedSearches: () => void;
  filter: Filter;
  /** The map's rectangle, recorded on a search saved with no area slug to locate it. */
  mapBounds?: MapBounds | null;
  /** True on a page where the map is the search; a saved search drops any slug. */
  locatedByMap?: boolean;
  /** Called when the user picks "All Saved Searches" in the saved-search sheet. */
  onViewAllSavedSearches?: () => void;
  navConfig: NavigationConfig;
  /** Tenant logo shown at the top of the fullscreen "Menu" sheet. */
  logoSrc: string;
  logoAlt: string;
  /** Renders the enlarged eXp lockup treatment instead of the compact Zoocasa wordmark. */
  isExpLogo?: boolean;
  /** Hides the whole bar — e.g. while the filters drawer covers the screen. */
  isHidden?: boolean;
  /** Which nav destinations to show — callers without Collections/Insights content can hide them. Defaults to all three. */
  availableDestinations?: RailDestination[];
  mapLabel?: string;
  collectionsLabel?: string;
  insightsLabel?: string;
  navLabel?: string;
  savedSearchesLabel?: string;
  menuLabel?: string;
  /** Adds a "Language" entry with the tenant's locale options to the fullscreen menu. */
  localeMenu?: RailLocaleMenu;
};

/**
 * Mobile equivalent of the desktop `LeftRail` + `TopBar`'s bookmark button —
 * same destinations/handlers/menu, laid out as a floating horizontal bar instead
 * of a vertical sidebar. Owns the Saved Searches sheet's open state, mirroring how
 * `TopBar` owns its popover anchor locally.
 */
export function MobileNavBar({
  destination,
  onOpenMap,
  onOpenCollections,
  onOpenInsights,
  onOpenSavedSearches,
  filter,
  mapBounds,
  locatedByMap,
  onViewAllSavedSearches,
  navConfig,
  logoSrc,
  logoAlt,
  isExpLogo = false,
  isHidden = false,
  availableDestinations = DEFAULT_AVAILABLE_DESTINATIONS,
  mapLabel = 'Map',
  collectionsLabel = 'Collections',
  insightsLabel = 'Insights',
  navLabel = 'Area page',
  savedSearchesLabel = 'Saved searches',
  menuLabel,
  localeMenu,
}: MobileNavBarProps) {
  const [isSavedSearchesOpen, setIsSavedSearchesOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Exactly one of the 4 icons (Map/Collections/Insights/Menu) is active at a time:
  // the open fullscreen menu takes precedence over whichever destination is current.
  const isMap = destination === 'map' && !isMenuOpen;
  const isCollections = destination === 'collections' && !isMenuOpen;
  const isInsights = destination === 'insights' && !isMenuOpen;

  const handleOpenSavedSearches = useCallback(() => {
    setIsSavedSearchesOpen(true);
    onOpenSavedSearches();
  }, [onOpenSavedSearches]);

  const handleCloseSavedSearches = useCallback(() => {
    setIsSavedSearchesOpen(false);
  }, []);

  return (
    <>
      {!isHidden && (
        <nav className={styles.root} data-testid="AreaPageV2-MobileNavBar" aria-label={navLabel}>
          <div className={styles.group}>
            <button
              type="button"
              className={buildClassName(styles.item, isMap && styles.itemActive)}
              data-testid="AreaPageV2-MobileNavBar-Map"
              aria-current={isMap ? 'page' : undefined}
              aria-label={mapLabel}
              onClick={onOpenMap}
            >
              <Map className={styles.icon} strokeWidth={2.25} aria-hidden />
            </button>
            {availableDestinations.includes('collections') && (
              <button
                type="button"
                className={buildClassName(styles.item, isCollections && styles.itemActive)}
                data-testid="AreaPageV2-MobileNavBar-Collections"
                aria-current={isCollections ? 'page' : undefined}
                aria-label={collectionsLabel}
                onClick={onOpenCollections}
              >
                <Heart className={styles.icon} strokeWidth={1.9} aria-hidden />
              </button>
            )}
            {availableDestinations.includes('insights') && (
              <button
                type="button"
                className={buildClassName(styles.item, isInsights && styles.itemActive)}
                data-testid="AreaPageV2-MobileNavBar-Insights"
                aria-current={isInsights ? 'page' : undefined}
                aria-label={insightsLabel}
                onClick={onOpenInsights}
              >
                <Sparkles className={styles.icon} strokeWidth={1.9} aria-hidden />
              </button>
            )}
            <RailMenu
              navConfig={navConfig}
              mobileVariant
              logoSrc={logoSrc}
              logoAlt={logoAlt}
              isExpLogo={isExpLogo}
              menuLabel={menuLabel}
              onOpenChange={setIsMenuOpen}
              localeMenu={localeMenu}
            />
          </div>
          {isMap && (
            <button
              type="button"
              className={buildClassName(styles.bookmarkButton, isSavedSearchesOpen && styles.bookmarkButtonActive)}
              onClick={handleOpenSavedSearches}
              data-testid="AreaPageV2-MobileNavBar-SavedSearches"
              aria-label={savedSearchesLabel}
            >
              <Bookmark className={styles.icon} strokeWidth={2} aria-hidden />
            </button>
          )}
        </nav>
      )}
      {isSavedSearchesOpen && (
        <MobileSavedSearchSheet onClose={handleCloseSavedSearches} currentFilter={filter} mapBounds={mapBounds} locatedByMap={locatedByMap} onViewAllSavedSearches={onViewAllSavedSearches} />
      )}
    </>
  );
}
